"use client";

import React, { useState } from "react";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, Sector, PieLabelRenderProps } from "recharts";

/* keep legacy colours / order for visual parity with the old jQuery tool */
const COLORS = ["#7cb5ec", "#434348"];

/* money formatter used everywhere in this file */
const toCurrency = (n: number) =>
    `$${n.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;

/* 🔸 active‑slice renderer -------------------------------------------- */
/* recharts types activeShape as `(p: unknown) => ReactElement`          */
const ActiveSlice = (props: unknown) => (
    <Sector {...(props as Record<string, unknown>)} stroke="#fff" strokeWidth={3} />
);

/* Props – optional fields let us reuse the same component  */
export interface MortgagePieChartProps {
    /* ① PURCHASE‑SIDE BREAKDOWN */
    loanAmount?: number;
    downPayment?: number;

    /* ② MONTHLY‑PAYMENT BREAKDOWN */
    principalAndInterest?: number;
    taxesAndInsurance?: number;
}

const MortgagePieChart: React.FC<MortgagePieChartProps> = ({
    /* purchase */
    loanAmount,
    downPayment,

    /* payment */
    principalAndInterest,
    taxesAndInsurance,
}) => {
    /* HOOK FIRST — runs on every render, no matter what */
    const [active, setActive] = useState<number | undefined>();

    /* build the dataset */
    let data: { name: string; value: number }[] = [];

    if (
        loanAmount !== undefined &&
        downPayment !== undefined &&
        !Number.isNaN(loanAmount) &&
        !Number.isNaN(downPayment)
    ) {
        /* case ① – affordability: Loan vs. Down‑Payment */
        data = [
            { name: "Loan Amount", value: loanAmount },
            { name: "Down Payment", value: downPayment },
        ];
    } else if (principalAndInterest !== undefined && taxesAndInsurance !== undefined) {
        /* case ② – monthly payment breakdown */
        data = [
            { name: "Loan Amount", value: principalAndInterest },
            { name: "Down Payment", value: taxesAndInsurance },
        ];
    }

    /* if nothing to show, bail out (hooks already executed) */
    if (data.length === 0) return null;

    const total = data.reduce((s, d) => s + d.value, 0);

    /* label rendered on each slice */
    const renderLabel = ({ value }: PieLabelRenderProps) =>
        `${toCurrency(Number(value))} (${((Number(value) / total) * 100).toFixed(1)}%)`;

    /* JSX -------------------------------------------------------------- */
    return (
        <div className="mt-8">
            <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                    <Pie
                        data={data}
                        dataKey="value"
                        cx="50%"
                        cy="50%"
                        outerRadius={150}
                        paddingAngle={4}
                        labelLine={false}
                        label={renderLabel}
                        activeIndex={active}
                        activeShape={ActiveSlice}
                        onMouseEnter={(_, i) => setActive(i)}
                        onMouseLeave={() => setActive(undefined)}
                        isAnimationActive
                        animationDuration={1000}
                    >
                        {data.map((d, i) => (
                            <Cell key={d.name} fill={COLORS[i % COLORS.length]} />
                        ))}
                    </Pie>

                    <Tooltip formatter={(v: number) => `${toCurrency(v)} (${((v / total) * 100).toFixed(1)}%)`} />

                    <Legend
                        iconType="circle"
                        align="center"
                        verticalAlign="bottom"
                        formatter={(name: string) => {
                            const slice = data.find((d) => d.name === name);
                            if (!slice) return name;
                            const pct = ((slice.value / total) * 100).toFixed(1);
                            return `${name} – ${toCurrency(slice.value)} (${pct}%)`;
                        }}
                    />
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
};

export default MortgagePieChart;
